import { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Paginator } from 'primereact/paginator';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { useApi } from '../providers/api-provider';
import { useAuth } from '../providers/auth-provider';
import { AdminCertificateDto, AdminCertificatesDto } from '../general/dtos';
import { useBookmarkSelection } from '../general/bookmark-selection';
import '../assets/admin-certificates.css';
import { ContentPage } from '../components/content-page';

export default function AdminCertificatesPage() {
    const api = useApi();
    const identity = useAuth().getUser()?.sub;
    const [key, selectCertificate] = useBookmarkSelection('certificate');
    const [pageKey, selectPage] = useBookmarkSelection('page');
    const [userFilter, selectUserFilter] = useBookmarkSelection('user');
    const [courseFilter, selectCourseFilter] = useBookmarkSelection('course');
    const [statusFilter, selectStatusFilter] = useBookmarkSelection('status');
    const status = ['granted', 'revoked'].includes(statusFilter ?? '') ? statusFilter! : 'all';
    const page = /^[1-9][0-9]{0,5}$/.test(pageKey ?? '') ? Number(pageKey) : 1;
    const [list, setList] = useState<AdminCertificatesDto | null>(null);
    const [listError, setListError] = useState('');
    const [loading, setLoading] = useState(false);
    const [certificate, setCertificate] = useState<AdminCertificateDto | null>(null);
    const [detailsError, setDetailsError] = useState('');
    const [preview, setPreview] = useState<{ key: string; url: string } | null>(null);
    const [previewError, setPreviewError] = useState('');
    const [saving, setSaving] = useState(false);
    const [statusError, setStatusError] = useState('');
    const [notice, setNotice] = useState('');
    const [revision, setRevision] = useState(0);
    const [previewRevision, setPreviewRevision] = useState(0);
    const busy = useRef(false);
    const mounted = useRef(false);
    const current = useRef({ key, identity });
    current.current = { key, identity };
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

    useEffect(() => {
        if (!api) return;
        const controller = new AbortController();
        setLoading(true); setListError('');
        const timer = window.setTimeout(() => {
            api.getAdminCertificates(page, { user: userFilter ?? '', course: courseFilter ?? '', status }, controller.signal).then(result => {
                if (!controller.signal.aborted) setList(result);
            }).catch(() => {
                if (!controller.signal.aborted) setListError('Could not load certificates. Please try again.');
            }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
        }, 250);
        return () => { window.clearTimeout(timer); controller.abort(); };
    }, [api, identity, page, revision, userFilter, courseFilter, status]);

    useEffect(() => {
        const controller = new AbortController();
        setCertificate(null); setDetailsError(''); setStatusError(''); setNotice('');
        if (api && key) api.getAdminCertificate(key, controller.signal).then(result => {
            if (!controller.signal.aborted) setCertificate(result);
        }).catch(() => {
            if (!controller.signal.aborted) setDetailsError('Could not load this certificate. It may no longer exist.');
        });
        return () => controller.abort();
    }, [api, identity, key]);

    useEffect(() => {
        const controller = new AbortController();
        let objectUrl: string | undefined;
        setPreview(null); setPreviewError('');
        if (api && key && certificate?.key === key && certificate.recipient_name !== null) api.getAdminCertificatePreview(key, controller.signal).then(blob => {
            if (controller.signal.aborted) return;
            objectUrl = URL.createObjectURL(blob);
            setPreview({ key, url: objectUrl });
        }).catch(() => {
            if (!controller.signal.aborted) setPreviewError('Could not generate the certificate preview. Please try again.');
        });
        return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [api, identity, key, previewRevision, certificate?.key, certificate?.recipient_name]);

    const changeStatus = async () => {
        if (!api || !certificate || busy.current) return;
        const target = certificate;
        busy.current = true; setSaving(true); setStatusError(''); setNotice('');
        try {
            const updated = await api.setCertificateRevoked(target.key, !target.revoked_at);
            if (!mounted.current || current.current.identity !== identity) return;
            setRevision(value => value + 1);
            if (current.current.key === target.key) {
                setCertificate(updated);
                setNotice(updated.revoked_at ? 'Certificate revoked. Its public link is no longer available.'
                    : updated.recipient_name === null ? 'Certificate granted. Awaiting the learner’s certificate name confirmation.' : 'Certificate granted. Its public link is available again.');
            }
        } catch (error: any) {
            if (mounted.current && current.current.identity === identity && current.current.key === target.key)
                setStatusError(error?.response?.data?.message || 'Could not change the certificate status. Please try again.');
        } finally {
            busy.current = false;
            if (mounted.current) setSaving(false);
        }
    };

    return <div className="admin-certificates">
        <ContentPage pageId="admin-certificates" pageTitle="Certificates" pageTitleLevel="h1"
            leftPanelTitle="Certificates" rightPanelTitle="Certificate preview" leftContentPercent={40}
            mobileTabs initialMobilePanel={key ? 'right' : 'left'} mobileBackLabel="Back to certificates"
            mobileRightPanelTitle="Preview"
            leftContent={showDetails => <section className="admin-certificates-pane" aria-label="Certificate list">
                {listError && <div role="alert">{listError} <Button label="Retry list" text onClick={() => setRevision(value => value + 1)} /></div>}
                <DataTable value={list?.data ?? []} loading={loading && !list} selectionMode="single" dataKey="key" lazy filterDisplay="row"
                    selection={list?.data.find(row => row.key === key) ?? null} scrollable scrollHeight="flex" emptyMessage="No certificates found."
                    onSelectionChange={event => {
                        if (busy.current) return;
                        if (event.value) selectCertificate((event.value as AdminCertificateDto).key);
                        if (event.value || key) showDetails();
                    }}>
                    <Column field="recipient_name" header="Learner" filter showFilterMenu={false} showClearButton={false}
                        filterElement={<InputText aria-label="Filter by user" placeholder="User name" value={userFilter ?? ''} maxLength={250} disabled={saving}
                            onChange={event => { selectUserFilter(event.target.value || null, true); selectPage(null, true); }} />} />
                    <Column field="course_name" header="Course" filter showFilterMenu={false} showClearButton={false}
                        filterElement={<InputText aria-label="Filter by course" placeholder="Course name" value={courseFilter ?? ''} maxLength={250} disabled={saving}
                            onChange={event => { selectCourseFilter(event.target.value || null, true); selectPage(null, true); }} />} />
                    <Column field="revoked_at" header="Status" filter showFilterMenu={false} showClearButton={false}
                        filterElement={<Dropdown aria-label="Filter by status" value={status} disabled={saving}
                            options={[{ label: 'All', value: 'all' }, { label: 'Granted', value: 'granted' }, { label: 'Revoked', value: 'revoked' }]}
                            onChange={event => { selectStatusFilter(event.value === 'all' ? null : event.value, true); selectPage(null, true); }} />}
                        body={(row: AdminCertificateDto) => row.revoked_at ? 'Revoked' : 'Granted'} />
                </DataTable>
                <Paginator first={((list?.page ?? page) - 1) * 25} rows={25} totalRecords={list?.total ?? 0}
                    onPageChange={event => { if (!busy.current) selectPage(event.page ? String(event.page + 1) : null); }} />
            </section>}
            rightContent={<section className="admin-certificates-pane" aria-label="Certificate preview">
                <h2>{certificate?.key === key ? certificate.course_name : 'Preview'}</h2>
                {!key && <p>Select a certificate to preview it and change its status.</p>}
                {detailsError && <p role="alert">{detailsError}</p>}
                {certificate?.key === key && <div className="admin-certificate-controls admin-toolbar">
                    <span>{certificate.recipient_name} · {certificate.revoked_at ? 'Revoked' : 'Granted'}</span>
                    <Button label={certificate.revoked_at ? 'Grant' : 'Revoke'} icon={certificate.revoked_at ? 'pi pi-check' : 'pi pi-ban'}
                        severity={certificate.revoked_at ? 'success' : 'danger'} loading={saving} onClick={() => void changeStatus()} />
                </div>}
                {statusError && <p role="alert">{statusError}</p>}
                {notice && <p role="status">{notice}</p>}
                {certificate?.key === key && certificate?.recipient_name === null && <p role="status">Awaiting the learner’s certificate name confirmation.</p>}
                {key && certificate?.recipient_name !== null && !preview && !previewError && <p role="status">Generating preview…</p>}
                {previewError && <div role="alert">{previewError} <Button label="Retry preview" text onClick={() => setPreviewRevision(value => value + 1)} /></div>}
                {preview?.key === key && <img className="admin-certificate-image" src={preview.url} alt={`Certificate for ${certificate?.recipient_name ?? 'selected learner'}`}
                    onError={() => { setPreview(null); setPreviewError('Could not display the certificate preview.'); }} />}
            </section>} />
    </div>;
}
